'use client'
import { useEffect, useState, useMemo, useRef } from 'react'
import BreadCumb from './BreadCumb'
import Pagination2 from '../common/Pagination2'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import Link from 'next/link'
import Image from 'next/image'
import { useContextElement } from '@/context/Context'
import { openModalShopFilter } from '@/utils/aside'
import ShopFilter from '@/components/asides/ShopFilter'

import { useSearchParams, useRouter } from 'next/navigation'
import { signIn } from 'next-auth/react'

import {
  sortingOptions,
  pageSizeOptions,
} from '@/data/products/productCategories'
import FilterAll from './filter/FilterAll'
import ColorSelection from '../common/ColorSelection'
import Star from '../common/Star'
import QuickViewButton from './QuickViewButton'
import usePersistedPageSize from '@/hooks/usePersistedPageSize'
import OptionPicker from './OptionPicker'
import styles from '@/components/shoplist/Shop2.module.css'
import ShopPageProductCard from './ShopPageProductCard'

/** ===================== utils ===================== **/

const pickArray = (json) => {
  if (!json) return []
  if (Array.isArray(json)) return json
  if (Array.isArray(json.Data)) return json.Data
  if (Array.isArray(json.data)) return json.data
  if (Array.isArray(json.List)) return json.List
  if (Array.isArray(json.Items)) return json.Items
  if (Array.isArray(json.ProductList)) return json.ProductList
  if (Array.isArray(json?.Data?.Data)) return json.Data.Data
  return []
}

const pickCount = (json) => {
  if (!json) return 0
  const candidates = [
    json?.ProductCount,
    json?.Data?.ProductCount,
    json?.TotalCount,
    json?.Data?.TotalCount,
  ]
  for (const v of candidates) {
    if (typeof v === 'number' && !Number.isNaN(v)) return v
  }
  if (Array.isArray(json?.Data)) return json.Data.length
  if (Array.isArray(json?.data)) return json.data.length
  return 0
}

const ALLOWED_SORTS = [2, 4, 5]
const normalizeSort = (v) => (ALLOWED_SORTS.includes(Number(v)) ? Number(v) : 2)

const ABS_URL = /^https?:\/\//i
const IMAGE_BASE =
  process.env.NEXT_PUBLIC_IMAGE_ORIGIN || 'http://192.168.193.33:25000'
const rewriteToImageBase = (name) => {
  if (!name) return ''
  try {
    let pathname = name
    if (ABS_URL.test(name)) {
      const u = new URL(name)
      pathname = u.pathname
    }
    if (!pathname.startsWith('/')) pathname = `/${pathname}`
    return `${IMAGE_BASE}${pathname}`
  } catch {
    return `${IMAGE_BASE}${name.startsWith('/') ? '' : '/'}${name}`
  }
}

const normalizeForCart = (p) => baseCartShape(p) // 你原本就這樣，我不動它

// ✅ 小工具：從 sku / product 取價格（容錯）
const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const calcSellableQty = (obj) => {
  const inv = toNumber(obj?.InventoryQty) ?? 0
  const safe = toNumber(obj?.SafetyInventoryQty) ?? 0
  return inv - safe
}

const isOutOfStock = (p) => {
  // 如果有 SKU：只要任一 SKU 可賣 > 0 就不算缺貨
  const skus = Array.isArray(p?.ProductOption) ? p.ProductOption : []
  if (skus.length) {
    const anyInStock = skus.some((sku) => sku.InventoryQty > 0)
    return !anyInStock
  }

  // 沒 SKU：看商品層級可賣庫存
  return p.InventoryQty <= 0
}

const pickSkuPrice = (sku, p) => {
  const v =
    sku?.Price ??
    sku?.SalePrice ??
    sku?.SellPrice ??
    sku?.UnitPrice ??
    sku?.FinalPrice ??
    sku?.DiscountPrice
  const n = toNumber(v)
  if (n !== null) return n
  const pn = toNumber(p?.Price)
  return pn !== null ? pn : 0
}

const pickSkuOldPrice = (sku, p) => {
  const v =
    sku?.SuggestPrice ??
    sku?.OriginPrice ??
    sku?.OriginalPrice ??
    sku?.ListPrice ??
    sku?.MarketPrice ??
    sku?.MSRP ??
    p?.SuggestPrice
  const n = toNumber(v)
  return n !== null ? n : null
}

// ✅ 改：以 SKU 為準寫入購物車資料
const normalizeForCartWithSku = (p, skuObj, unitPriceFromPicker) => {
  const skuPrice =
    toNumber(unitPriceFromPicker) !== null
      ? Number(unitPriceFromPicker)
      : pickSkuPrice(skuObj, p)
  const skuOld = pickSkuOldPrice(skuObj, p)
  return {
    ProductID: p.ProductID,
    ProductName: p.ProductName,

    // ✅ 用 SKU 價格
    Price: skuPrice,
    SuggestPrice: skuOld ?? p.SuggestPrice,

    Cost: p.Cost,
    PriceFromProduct: p.Price,

    PictureData: p.PictureData,

    SkuID: skuObj?.SkuID ?? p.SkuID,
    SkuName: skuObj?.SkuName,

    // ✅ 庫存/限購用 SKU
    InventoryQty: skuObj?.InventoryQty ?? p.InventoryQty,
    LimitedQty: skuObj?.LimitedQty,

    ProductOption: p.ProductOption ?? [],

    IsPreorder: p?.IsPreorder,
  }
}

const priceNode = (price, old) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
    {old && Number(old) > Number(price) ? (
      <span style={{ textDecoration: 'line-through', color: '#999' }}>
        ${Number(old || 0).toLocaleString()}
      </span>
    ) : null}
    <span style={{ fontWeight: 700 }}>
      ${Number(price || 0).toLocaleString()}
    </span>
  </div>
)

const ProductListLoading = () => (
  <div className={styles.productLoadingBox}>
    <div className={styles.productLoadingSpinner}></div>
    <div className={styles.productLoadingText}>商品載入中...</div>
  </div>
)

/** ===================== component ===================== **/

export default function Shop2({ categories, brands, storeInfo, keyword }) {
  const {
    toggleWishlist,
    isAddedtoWishlist,
    addProductToCart,
    isAddedToCartProducts,
    cartProducts,
    wishList,
  } = useContextElement()
  const router = useRouter()
  const searchParams = useSearchParams()

  const spString = searchParams.toString()

  const lineIdFromQuery =
    searchParams.get('lineId') || searchParams.get('lineid') || ''

  const currentCategory = searchParams.get('categoryid')
  const currentBrandID = searchParams.get('brandid')

  const [selectedColView, setSelectedColView] = useState(4)
  const itemPerRow = [2, 3, 4]

  const [category, setCategory] = useState([])
  const [brand, setBrand] = useState([])
  const [priceMin, setPriceMin] = useState(0)
  const [priceMax, setPriceMax] = useState(99999)

  const [productList, setProductList] = useState([])
  const [productCount, setProductCount] = useState(0)
  const [pageCount, setPageCount] = useState(1)
  const currentPage = Number(searchParams.get('page')) || 1
  const [pageSize, setPageSize] = usePersistedPageSize(32)

  const [isLoading, setIsLoading] = useState(true)

  const [filters, setFilters] = useState({
    category: '',
    brandId: '',
    minPrice: 0,
    maxPrice: 99999,
    sortBy: 2,
  })

  const [addingId, setAddingId] = useState(null)

  // 規格挑選視窗
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerProduct, setPickerProduct] = useState(null)

  // ✅ 新增：彈窗上方 header 要顯示的「SKU 價格」
  const [pickerSkuObj, setPickerSkuObj] = useState(null)
  const [pickerSkuPrice, setPickerSkuPrice] = useState(null)
  const [pickerSkuOldPrice, setPickerSkuOldPrice] = useState(null)

  // Toast
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' })
  const toastTimerRef = useRef(null)
  const showToast = (msg, type = 'success', duration = 2200) => {
    setToast({ show: true, msg, type })
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(
      () => setToast((t) => ({ ...t, show: false })),
      duration
    )
  }

  useEffect(
    () => () => toastTimerRef.current && clearTimeout(toastTimerRef.current),
    []
  )

  useEffect(() => {
    if (categories) setCategory(categories)
  }, [categories])

  useEffect(() => {
    if (brands) setBrand(brands)
  }, [brands])

  const allowedSortingOptions = useMemo(
    () =>
      Array.isArray(sortingOptions)
        ? sortingOptions.filter((o) => ALLOWED_SORTS.includes(Number(o.value)))
        : [],
    []
  )

  // 商品外框設定
  const cardBorderEnable = storeInfo?.ProductCardBorderEnable ?? false
  const cardBorderColor = storeInfo?.ProductCardBorderColor ?? '#000'
  const cardBorderWidth = storeInfo?.ProductCardBorderWidth ?? 1
  const cardBorderRound = storeInfo?.ProductCardBorderRound ?? 0

  // ✅ lineId-only 自動登入（只跑一次）
  const autoLoginOnceRef = useRef(false)
  useEffect(() => {
    const uid = (lineIdFromQuery || '').trim()
    if (!uid) return
    if (autoLoginOnceRef.current) return
    autoLoginOnceRef.current = true
    ;(async () => {
      try {
        await signIn('credentials', {
          redirect: false,
          lineId: uid,
        })
      } catch (e) {
        console.warn('Line auto login failed:', e)
      } finally {
        const p = new URLSearchParams(spString)
        p.delete('lineId')
        p.delete('lineid')
        const qs = p.toString()
        router.replace(`/product-list${qs ? `?${qs}` : ''}`, { scroll: false })
      }
    })()
  }, [lineIdFromQuery, spString, router])

  const judgeIsAddWishList = (id) => {
    const isAdded = wishList.some((item) => item.ProductID === id)
    return isAdded ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M8 1.314C12.438-3.248 23.534 4.735 8 15-7.534 4.736 3.562-3.248 8 1.314z" />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M8 2.748l-.717-.737C5.6.281 2.514.878 1.4 3.053c-.523 1.023-.641 2.5.314 4.385.92 1.815 2.834 3.989 6.286 6.357 3.452-2.368 5.365-4.542 6.286-6.357.955-1.886.838-3.362.314-4.385C13.486.878 10.4.28 8.717 2.01L8 2.748zM8 15C-7.333 4.868 3.279-3.04 7.824 1.143c.06.055.119.112.176.171a3.12 3.12 0 0 1 .176-.17C12.72-3.042 23.333 4.867 8 15z" />
      </svg>
    )
  }

  /** ------------------- APIs ------------------- **/

  const fetchDataCategory = async () => {
    try {
      const params = new URLSearchParams({
        StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER ?? '',
      })
      const res = await fetch(`/api/product/get_category?${params.toString()}`)
      const json = await res.json()
      setCategory(pickArray(json))
    } catch (e) {
      console.error('get_Category error:', e)
    }
  }

  const fetchDataProductCount = async () => {
    try {
      const sort = normalizeSort(filters.sortBy)
      const categoryId = searchParams.get('categoryid')
      const brandId = searchParams.get('brandid')

      const params = new URLSearchParams({
        StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER ?? '',
        SortBy: String(sort),
        PageNo: String(currentPage),
        DisplayCount: String(pageSize),
        MinPrice: String(filters.minPrice),
        MaxPrice: String(filters.maxPrice),
      })

      if (categoryId) params.set('CategoryID', String(categoryId))
      if (brandId) params.set('BrandID', String(brandId))
      if (keyword) params.set('Keyword', String(keyword))

      const res = await fetch(
        `/api/product/get_ProductCount?${params.toString()}`
      )
      const json = await res.json()
      const count = pickCount(json)

      setProductCount(count)
      setPageCount(Math.max(Math.ceil(count / (pageSize || 1)), 1))
    } catch (e) {
      console.error('get_ProductCount error:', e)
    }
  }

  const fetchDataProductList = async () => {
    try {
      const sort = normalizeSort(filters.sortBy)
      const categoryId = searchParams.get('categoryid')
      const brandId = searchParams.get('brandid')

      const params = new URLSearchParams({
        StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER ?? '',
        SortBy: String(sort),
        PageNo: String(Number(searchParams.get('page')) || 1),
        DisplayCount: String(pageSize),
        MinPrice: String(filters.minPrice),
        MaxPrice: String(filters.maxPrice),
      })

      if (categoryId) params.set('CategoryID', String(categoryId))
      if (brandId) params.set('BrandID', String(brandId))
      if (keyword) params.set('Keyword', String(keyword))

      const res = await fetch(
        `/api/product/get_ProductList?${params.toString()}`
      )
      const json = await res.json()
      const nextList = pickArray(json)

      setProductList(nextList)
    } catch (e) {
      console.error('get_ProductList error:', e)
      setProductList([])
    }
  }

  /** 後端加入購物車 **/
  const addToCartApi = async ({ ProductID, SkuID = 0, Qty = 1 }) => {
    const url = `/api/product/add_Product_To_Cart`
    const body = {
      MemberID: 1,
      ProductData: [
        {
          ProductID: Number(ProductID),
          SkuID: Number(SkuID) || 0,
          Qty: Number(Qty) || 1,
        },
      ],
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`add_product_to_cart failed (${res.status})`)
    try {
      return await res.json()
    } catch {
      return { ok: true }
    }
  }

  const fetchCartListOnce = async () => {
    try {
      const res = await fetch('/api/product/get_Cart_Product', {
        method: 'GET',
      })
      await res.json().catch(() => ({}))
    } catch {}
  }

  const handleAddClick = (p) => {
    if (!p?.ProductID || addingId) return
    setPickerProduct(p)

    // ✅ 打開彈窗先清掉 SKU 價格狀態（避免沿用上一次）
    setPickerSkuObj(null)
    setPickerSkuPrice(null)
    setPickerSkuOldPrice(null)

    setPickerOpen(true)
  }

  /** --------- 每改變頁碼就改變 URL --------- **/
  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('page', page)
    window.scrollTo({ top: 0, behavior: 'instant' })
    router.push(`/product-list?${params.toString()}`, { scroll: false })
  }

  /** ------------------- Effects ------------------- **/

  useEffect(() => {
    fetchDataCategory()
  }, [])

  useEffect(() => {
    if (currentCategory) {
      setFilters((prev) => ({
        ...prev,
        category: currentCategory,
        brandId: null,
      }))
    }
  }, [currentCategory])

  useEffect(() => {
    if (currentBrandID) {
      setFilters((prev) => ({
        ...prev,
        brandId: currentBrandID,
        category: null,
      }))
    }
  }, [currentBrandID])

  useEffect(() => {
    setFilters((prev) => ({ ...prev, sortBy: normalizeSort(prev.sortBy) }))
  }, [])

  useEffect(() => {
    setProductList([])
    setProductCount(0)
    setIsLoading(true)

    Promise.all([fetchDataProductList(), fetchDataProductCount()]).finally(
      () => {
        setIsLoading(false)
      }
    )
  }, [filters, currentPage, pageSize, currentCategory, currentBrandID, keyword])

  useEffect(() => {
    if (!pickerOpen) return
    const prev = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') setPickerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.documentElement.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  useEffect(() => {
    if (currentPage === 1 && productList && productList.length < pageSize) {
      setPageCount(1)
    }
  }, [currentPage, productList, pageSize])

  /** ------------------- UI ------------------- **/

  return (
    <>
      <div className="mb-4 pb-lg-3 pt-sm-5 pt-lg-5 pt-xl-2 "></div>
      <section className="shop-main container d-flex">
        {/* 側邊篩選 */}
        <div className="shop-sidebar side-sticky bg-body">
          <div
            onClick={openModalShopFilter}
            className="aside-header d-flex d-lg-none align-items-center"
          >
            <h3 className="text-uppercase fs-6 mb-0">篩選條件</h3>
            <button className="btn-close-lg js-close-aside btn-close-aside ms-auto"></button>
          </div>
          <div className="pt-4 pt-lg-0"></div>
          <FilterAll
            categories={category}
            currentCategoryID={filters.category}
            currentBrandID={filters.brandId}
            brands={brand}
            currentActiveBrands={[]}
            onChangeBrands={() => {}}
            onChangePrice={(range) =>
              setFilters((prev) => ({
                ...prev,
                minPrice: range[0],
                maxPrice: range[1],
              }))
            }
            priceMin={priceMin}
            priceMax={priceMax}
            onChangeCategory={(catId) => {
              setFilters((prev) => ({ ...prev, category: catId || '' }))
            }}
          />
        </div>

        {/* 商品列表 */}
        <div className="shop-list flex-grow-1">
          <div className="d-flex justify-content-between mb-4 pb-md-2">
            <div className="breadcrumb mb-0 d-none d-md-block flex-grow-1">
              <BreadCumb />
            </div>

            {/* 上方控制列 */}
            <div className="shop-acs d-flex align-items-center justify-content-between justify-content-md-end flex-grow-1">
              <div className="p-2 d-flex">
                <label
                  className="text-uppercase fw-medium me-2 p-1"
                  htmlFor="page-size-number"
                >
                  每頁
                </label>
                <select
                  id="page-size-number"
                  className="shop-acs__select form-select w-auto border-0 py-0 order-1 order-md-0"
                  aria-label="每頁顯示筆數"
                  name="page-size-number"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {pageSizeOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="p-2 d-flex">
                <label
                  className="text-uppercase fw-medium me-2 p-1"
                  htmlFor="total-number"
                >
                  排序
                </label>
                <select
                  className="shop-acs__select form-select w-auto border-0 py-0 order-1 order-md-0"
                  aria-label="排序方式"
                  name="total-number"
                  value={filters.sortBy}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      sortBy: normalizeSort(e.target.value),
                    }))
                  }
                >
                  {allowedSortingOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="shop-asc__seprator mx-3 bg-light d-none d-md-block order-md-0"></div>

              <div className="col-size align-items-center order-1 d-none d-lg-flex">
                <span className="text-uppercase fw-medium me-2">
                  <i className="bi bi-grid"></i>
                </span>
                {itemPerRow.map((elm, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedColView(elm)}
                    className={`btn-link fw-medium me-2 js-cols-size ${selectedColView == elm ? 'btn-link_active' : ''}`}
                  >
                    {elm}
                  </button>
                ))}
              </div>

              <div className="shop-filter d-flex align-items-center order-0 order-md-3 d-lg-none me-2">
                <button
                  className="btn-link btn-link_f d-flex align-items-center ps-0 js-open-aside"
                  onClick={openModalShopFilter}
                >
                  <svg
                    className="d-inline-block align-middle me-2"
                    width="14"
                    height="10"
                    viewBox="0 0 14 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <use href="#icon_filter" />
                  </svg>
                  <span className="text-uppercase fw-medium d-inline-block align-middle">
                    篩選條件
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* 商品格狀 */}
          {isLoading ? (
            <ProductListLoading />
          ) : productList.length === 0 ? (
            <div className="text-center text-muted py-5">
              查無符合條件的商品
            </div>
          ) : (
            <div
              className={`products-grid row row-cols-2 row-cols-md-3 row-cols-lg-${selectedColView}`}
              id="products-grid"
            >
              {productList.map((elm, i) => (
                <ShopPageProductCard
                  key={elm?.ProductID ?? i}
                  elm={elm}
                  i={i}
                  currentPage={currentPage}
                  storeInfo={storeInfo}
                  cardBorderEnable={cardBorderEnable}
                  cardBorderWidth={cardBorderWidth}
                  cardBorderColor={cardBorderColor}
                  cardBorderRound={cardBorderRound}
                  addingId={addingId}
                  handleAddClick={handleAddClick}
                  isAddedToCartProducts={isAddedToCartProducts}
                  isOutOfStock={isOutOfStock}
                  setPickerOpen={setPickerOpen}
                  sourceUrl={`/product-list/${elm.ProductID}?page=${currentPage}`}
                />
              ))}
            </div>
          )}

          {!isLoading && productList && productList.length > 0 && (
            <Pagination2
              currentPage={currentPage}
              totalPages={pageCount}
              onChange={handlePageChange}
            />
          )}
        </div>

        <ShopFilter
          categories={category}
          currentCategoryID={filters.category}
          currentBrandID={filters.brandId}
          brands={brand}
          currentActiveBrands={[]}
          onChangeBrands={() => {}}
          onChangePrice={(range) =>
            setFilters((prev) => ({
              ...prev,
              minPrice: range[0],
              maxPrice: range[1],
            }))
          }
          priceMin={priceMin}
          priceMax={priceMax}
          onChangeCategory={(catId) => {
            setFilters((prev) => ({ ...prev, category: catId || '' }))
          }}
        />
      </section>

      {/* 規格挑選彈窗 */}
      {pickerOpen && (
        <div
          onClick={() => setPickerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 820,
              maxHeight: '85vh',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,.25)',
              padding: 16,
              position: 'relative',
            }}
          >
            <button
              aria-label="關閉"
              onClick={() => setPickerOpen(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 36,
                height: 36,
                border: '1px solid #e5e5e5',
                borderRadius: 8,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 20,
                lineHeight: '32px',
                zIndex: 4,
              }}
              title="關閉"
            >
              ×
            </button>

            {pickerProduct && (
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  alignItems: 'center',
                  paddingRight: 56,
                  marginBottom: 8,
                  position: 'relative',
                  zIndex: 3,
                  background: '#fff',
                }}
              >
                <Image
                  unoptimized
                  src={pickerProduct?.PictureData?.[0]?.Name || ''}
                  alt={pickerProduct?.ProductName || 'product'}
                  width={96}
                  height={96}
                  style={{ borderRadius: 8, objectFit: 'cover' }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 16,
                      lineHeight: 1.35,
                      marginBottom: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                    title={`${pickerProduct?.ProductName || ''}${pickerProduct?.IsPreorder ? ' (預購)' : ''}`}
                  >
                    {pickerProduct?.ProductName}
                    {pickerProduct?.IsPreorder && ' (預購)'}
                  </div>

                  {/* ✅ 這裡就是你紅箭頭那行：改成吃 SKU 價格 */}
                  {priceNode(
                    pickerSkuPrice ?? pickerProduct?.Price ?? 0,
                    pickerSkuOldPrice ?? pickerProduct?.SuggestPrice ?? 0
                  )}
                </div>
              </div>
            )}

            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 96,
                background: '#fff',
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <OptionPicker
                open={pickerOpen}
                product={pickerProduct}
                cardProduct={cartProducts}
                isPreorder={pickerProduct?.IsPreorder === true}
                onClose={() => setPickerOpen(false)}
                onSkuChange={(sku, unitPrice, oldPrice) => {
                  setPickerSkuObj(sku || null)
                  setPickerSkuPrice(toNumber(unitPrice))
                  setPickerSkuOldPrice(toNumber(oldPrice))
                }}
                onConfirm={async (
                  skuId,
                  qty,
                  limitQty,
                  invQty,
                  unitPrice,
                  chosenSku
                ) => {
                  if (!pickerProduct) return
                  try {
                    const limit = Number(limitQty)
                    const inv = Number(invQty)
                    const isPreorder = pickerProduct?.IsPreorder === true

                    if (Number.isFinite(limit) && limit > 0 && qty > limit) {
                      alert(`\n超過此商品購買上限: ${limit}，故無法加入購物車`)
                      return
                    }

                    // 預購商品不擋庫存
                    if (
                      !isPreorder &&
                      Number.isFinite(inv) &&
                      inv >= 0 &&
                      qty > inv
                    ) {
                      alert(`\n超過此商品庫存，故無法加入購物車`)
                      return
                    }

                    setAddingId(pickerProduct.ProductID)

                    const skuObj =
                      chosenSku ||
                      (pickerProduct?.ProductOption || []).find(
                        (s) => s.SkuID === skuId
                      ) ||
                      null

                    addProductToCart({
                      ProductID: pickerProduct.ProductID,
                      updated: {
                        ...normalizeForCartWithSku(
                          pickerProduct,
                          skuObj,
                          unitPrice
                        ),
                        Qty: Number(qty) || 1,
                        Quantity: Number(qty) || 1,
                      },
                    })

                    setPickerOpen(false)
                    // showToast('成功加入購物車', 'success')
                    fetchCartListOnce()
                  } catch (e) {
                    console.error(e)
                    // showToast('加入購物車失敗，請稍後再試', 'error', 2600)
                  } finally {
                    setAddingId(null)
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            background:
              toast.type === 'success'
                ? 'rgba(25,135,84,.95)'
                : 'rgba(220,53,69,.95)',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.18)',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '.3px',
          }}
        >
          {toast.msg}
        </div>
      )}
    </>
  )
}
